import axios from "axios";
import "./Buycredits.css";
import Navbar from "../Navbar/navbar";
import down from "../../assets/hamburgermenu_icons/down.svg";
import credits from "../../assets/hamburgermenu_icons/credits.svg";
import React, { useRef, useState } from "react";
import supabase from "../../supabaseClient/supabaseClient";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";

import { faqs } from "./faq";

const Buycredits = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [btnLoading, setBtnLoading] = useState({
    Starter: false,
    Essential: false,
    Pro: false,
  });

  // NEW: global blocking overlay state
  const [checkoutState, setCheckoutState] = useState({
    status: "idle", // 'idle' | 'creating' | 'redirecting' | 'error'
    activePack: null, // 'Starter' | 'Essential' | 'Pro' | null
    checkoutUrl: "",
    showFallback: false,
    message: "",
  });

  const redirectTimeoutRef = useRef(null);

  const handleMenuToggle = (state) => {
    setMenuOpen(state);
  };

  const clearRedirectTimer = () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  };

  const startRedirectFailsafe = () => {
    clearRedirectTimer();
    redirectTimeoutRef.current = setTimeout(() => {
      setCheckoutState((prev) => ({ ...prev, showFallback: true }));
    }, 8000);
  };

  const isBlocking =
    checkoutState.status === "creating" ||
    checkoutState.status === "redirecting";

  const startCheckout = async ({ packKey, productId }) => {
    // Guard: prevents double submits if overlay already active
    if (isBlocking) return;

    try {
      setBtnLoading((prev) => ({ ...prev, [packKey]: true }));
      setCheckoutState({
        status: "creating",
        activePack: packKey,
        checkoutUrl: "",
        showFallback: false,
        message: "Creating checkout session…",
      });

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Authentication required");

      // The server decides how many credits this pack grants.
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL}/checkout`,
        { productId },
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      const url = response.data?.checkoutUrl;
      if (!url) throw new Error("No checkoutUrl returned from server.");

      // Success path: KEEP UI blocked, show overlay, start failsafe, redirect
      setCheckoutState({
        status: "redirecting",
        activePack: packKey,
        checkoutUrl: url,
        showFallback: false,
        message: "Redirecting to secure checkout…",
      });

      startRedirectFailsafe();
      window.location.assign(url);

      // Important: do NOT set loading false here, because we WANT the UI locked
      // until navigation happens. If navigation fails, fallback link appears.
    } catch (error) {
      console.error("Checkout creation failed:", error);
      clearRedirectTimer();

      setCheckoutState({
        status: "error",
        activePack: null,
        checkoutUrl: "",
        showFallback: false,
        message: "Checkout failed. Please try again.",
      });

      // Only re-enable the clicked button on error
      setBtnLoading((prev) => ({ ...prev, [packKey]: false }));
    }
  };

  return (
    <div className="buy-page-body">
      <Navbar onMenuToggle={handleMenuToggle} />

      <div
        className={`books-container ${isMenuOpen ? "menu-open" : ""}`}
        style={{
          marginLeft: isMenuOpen ? "250px" : "0",
          transition: "margin-left 0.3s ease-in-out",
        }}
      >
        <div className="credits-header">
          <img src={credits} alt="buy_credits" className="credits-icon" />
          <h1 className="credits-title">Credit Packs</h1>
        </div>
        <aside className="credits-allowance-note" aria-label="Credits and AI allowance">
          <span className="credits-allowance-note-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
            </svg>
          </span>
          <div>
            <strong>Download credits and daily AI generations are separate.</strong>
            <p>A verified live credit purchase gives you the <span>higher daily AI allowance</span>, even after the credits are spent.</p>
          </div>
        </aside>

        <div className="div-for-all-pricing">
          <div className="pricing-card">
            <div className="book-details">
              <p className="pricing-title">Starter Pack</p>
              <p className="pricing-price">$1.50</p>
              <p className="pricing-description">10 Worksheet Downloads</p>

              <button
                data-gtm-event="track-click"
                data-gtm-data-label="starter-pack-clicked"
                className="pricing-button"
                onClick={() =>
                  startCheckout({
                    packKey: "Starter",
                    productId: process.env.REACT_APP_STARTER_VARIANT_ID,
                  })
                }
                disabled={btnLoading.Starter || isBlocking}
                aria-busy={btnLoading.Starter || isBlocking}
              >
                {btnLoading.Starter ? <span className="spinner" /> : "Buy Now"}
              </button>
            </div>
          </div>

          <div className="pricing-card">
            <div className="book-details">
              <p className="pricing-title">Standard Pack</p>
              <p className="pricing-price">$2.50</p>
              <p className="pricing-description">20 Worksheet Downloads</p>

              <button
                data-gtm-event="track-click"
                data-gtm-data-label="standard-pack-clicked"
                className="pricing-button"
                onClick={() =>
                  startCheckout({
                    packKey: "Essential",
                    productId: process.env.REACT_APP_STANDARD_VARIANT_ID,
                  })
                }
                disabled={btnLoading.Essential || isBlocking}
                aria-busy={btnLoading.Essential || isBlocking}
              >
                {btnLoading.Essential ? (
                  <span className="spinner" />
                ) : (
                  "Buy Now"
                )}
              </button>
            </div>
          </div>

          <div className="pricing-card">
            <div className="book-details">
              <p className="pricing-title">Bulk Pack</p>
              <p className="pricing-price">$5.50</p>
              <p className="pricing-description">50 Worksheet Downloads</p>

              <button
                data-gtm-event="track-click"
                data-gtm-data-label="pro-pack-clicked"
                className="pricing-button"
                onClick={() =>
                  startCheckout({
                    packKey: "Pro",
                    productId: process.env.REACT_APP_BULK_VARIANT_ID,
                  })
                }
                disabled={btnLoading.Pro || isBlocking}
                aria-busy={btnLoading.Pro || isBlocking}
              >
                {btnLoading.Pro ? <span className="spinner" /> : "Buy Now"}
              </button>
            </div>
          </div>
        </div>

        {/* Optional inline error (in addition to overlay disappearing on error) */}
        {checkoutState.status === "error" && (
          <p role="alert" className="checkout-error">
            {checkoutState.message}
          </p>
        )}

        <div className="faq-container">
          {faqs.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary
                expandIcon={
                  <img src={down} alt="down_icon" width={24} height={24} />
                }
              >
                <Typography variant="h6">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </div>

      {/* FULL SCREEN OVERLAY (Option B) */}
      {isBlocking && (
        <CheckoutOverlay
          message={checkoutState.message}
          checkoutUrl={checkoutState.checkoutUrl}
          showFallback={checkoutState.showFallback}
        />
      )}
    </div>
  );
};

export default Buycredits;

function CheckoutOverlay({ message, checkoutUrl, showFallback }) {
  return (
    <div
      className="checkout-overlay"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
    >
      <div className="checkout-overlay-card">
        <div className="checkout-overlay-row">
          <span className="spinner" />
          <div>
            <div className="checkout-overlay-title">{message}</div>
            <div className="checkout-overlay-subtitle">
              Please don’t close this tab.
            </div>
          </div>
        </div>

        {showFallback && checkoutUrl && (
          <div className="checkout-overlay-fallback">
            <div className="checkout-overlay-fallback-text">
              If you’re not redirected automatically:
            </div>
            <a className="checkout-overlay-link" href={checkoutUrl}>
              Continue to checkout
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
